<template>
  <div id="domains-manage">
    <v-dialog v-model="subdomains.modal" width="700">
      <v-card class="rounded-xl" elevation="7">
        <v-card-title> Create Subdomain/DNS Record</v-card-title>
        <v-card-text>
          You can create a subdomain which other users can set as their default
          under your domain. They will automatically point to
          {{ $store.state.site.name }} servers, you may however add your own
          record via the "Advanced Mode" and it will be excluded from the
          subdomains list.
        </v-card-text>
        <v-container>
          <v-row>
            <v-col cols="12" md="4" sm="6">
              <v-text-field
                class="rounded-xl"
                v-model="subdomains.recordName"
                label="Subdomain Name"
                placeholder="Example: mysubdomain"
              ></v-text-field>
            </v-col>
            <v-col v-if="subdomains.advanced" cols="12" md="4" sm="6">
              <v-select
                v-model="subdomains.recordType"
                :items="subdomains.types"
                item-value="text"
                label="Type"
              ></v-select>
            </v-col>
            <v-col v-if="subdomains.advanced" cols="12" md="4" sm="6">
              <v-text-field
                class="rounded-xl"
                v-model="subdomains.recordHost"
                label="IP/Hostname"
              ></v-text-field>
            </v-col>
            <v-col v-if="subdomains.advanced" cols="12" md="4" sm="6">
              <v-checkbox
                v-model="subdomains.proxied"
                label="Cloudflare Proxied"
              ></v-checkbox>
            </v-col>
          </v-row>
        </v-container>

        <v-divider></v-divider>

        <v-card-actions>
          <v-checkbox
            v-model="subdomains.advanced"
            disabled
            label="Advanced Mode (Coming Soon)"
          >
          </v-checkbox>
          <v-spacer></v-spacer>
          <v-btn
            class="rounded-xl"
            color="red"
            text
            @click="subdomains.modal = false"
          >
            Cancel
          </v-btn>
          <v-btn class="rounded-xl" color="primary" text @click="addRecord()">
            Add</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-container>
      <v-card v-if="status === 'pending'" class="rounded-xl" elevation="7">
        <v-container>
          <v-card-title>Your domain is still not active.</v-card-title>
          <v-form class="card">
            <v-card-text
              >Replace these nameservers:
              <pre
                v-for="nameserver in oldNameservers"
                :key="nameserver"
              ><code>{{nameserver}}</code></pre>
            </v-card-text>
            <v-card-text
              >with these nameservers:
              <pre
                v-for="nameserver in nameservers"
                :key="nameserver"
              ><code>{{nameserver}}</code></pre>
            </v-card-text>
            <v-btn class="rounded-xl" :loading="loading" @click="getCFNSStatus"
              >Recheck</v-btn
            >
          </v-form>
        </v-container>
      </v-card>
      <v-card v-if="status === 'active'" class="rounded-xl" elevation="7">
        <v-container>
          <v-card-title>Domain Settings</v-card-title>
          <v-checkbox
            v-model="subdomains.enabledCreate"
            label="Allow users to create subdomains"
          ></v-checkbox>
          <v-checkbox
            v-model="subdomains.enabled"
            label="Allow users to use subdomains"
          ></v-checkbox>
          <small
            >When the usage of subdomains is enabled, but creation isn't, there
            will be a selection of subdomains that can be used on the default
            screen to the user, on which you can create below.</small
          >
          <v-select
            v-model="domain.restricted"
            :items="restrictedTypes"
            item-text="name"
            item-value="internalName"
            label="Who can use your domain"
          ></v-select>
          <div v-if="domain.restricted === 'user'">
            <v-text-field
              class="rounded-xl"
              v-model="user"
              label="Username of User"
            >
            </v-text-field>
            <v-chip text-color="white" @click="addUserToDomain()">
              <v-icon>mdi-plus</v-icon>
              Add
            </v-chip>
            <br /><br />
            <v-data-table
              :headers="eligibleUsers.headers"
              :items="eligibleUsers.users"
              class="rounded-xl elevation-12"
            >
              <template v-slot:top>
                <v-toolbar class="rounded-xl">
                  <v-toolbar-title>Eligible Users</v-toolbar-title>
                  <v-divider class="mx-4" inset vertical></v-divider>
                  <v-spacer></v-spacer>
                </v-toolbar>
              </template>
              <template v-slot:item="row">
                <tr>
                  <td>{{ row.item.username }}</td>
                  <td>
                    <v-chip
                      color="red"
                      text-color="white"
                      @click="removeUserFromDomain(row.item)"
                    >
                      <v-icon left> mdi-minus</v-icon>
                      Remove User
                    </v-chip>
                  </td>
                </tr>
              </template>
            </v-data-table>
          </div>
          <br />
          <v-btn class="rounded-xl" @click="saveDomainSettings">Save</v-btn>
        </v-container>
      </v-card>
      <br />
      <v-card v-if="status === 'active'" class="rounded-xl" elevation="7">
        <v-container>
          <v-card-title>DNS Management</v-card-title>
          <v-data-table
            :headers="subdomains.headers"
            :items="subdomains.subdomains"
            class="rounded-xl elevation-12"
          >
            <template v-slot:top>
              <v-toolbar class="rounded-xl">
                <v-toolbar-title>Subdomains</v-toolbar-title>
                <v-divider class="mx-4" inset vertical></v-divider>
                <v-spacer></v-spacer>
                <v-chip text-color="white" @click="subdomains.modal = true">
                  <v-icon left> mdi-plus</v-icon>
                  Add Subdomain
                </v-chip>
              </v-toolbar>
            </template>
            <template v-slot:item="row">
              <tr>
                <td>{{ row.item.name }}</td>
                <td>{{ row.item.advanced }}</td>
                <td>
                  {{
                    dayjs(row.item.createdAt).format(
                      "Do of MMMM YYYY, h:mm:ss a"
                    )
                  }}
                </td>
                <td></td>
              </tr>
            </template>
          </v-data-table>
        </v-container>
      </v-card>
    </v-container>
  </div>
</template>

<script>
import AjaxErrorHandler from "@/lib/errorHandler"
import dayjs from "dayjs"
const advancedFormat = require("dayjs/plugin/advancedFormat")
dayjs.extend(advancedFormat)

export default {
  name: "DomainsManage",
  data() {
    return {
      enabled: true,
      enabledCreate: true,
      loading: false,
      status: "pending",
      domain: [],
      userSearch: "",
      user: "",
      eligibleUsers: {
        headers: [
          { text: "Username", value: "username" },
          { text: "Options", value: "options" }
        ],
        users: [],
        count: 0
      },
      userCount: 0,
      restricted: false,
      restrictedTypes: [
        {
          internalName: "disabled",
          name: "Everyone"
        },
        {
          internalName: "premium",
          name: "Premium and Advanced users"
        },
        {
          internalName: "user",
          name: "Select users"
        }
      ],
      subdomains: {
        loading: false,
        types: ["A", "AAAA", "CNAME", "MX"],
        advanced: false,
        recordHost: "",
        recordType: "",
        proxied: true,
        recordName: "",
        modal: false,
        headers: [
          { text: "Subdomain", value: "name" },
          { text: "Advanced", value: "advanced" },
          { text: "Created at", value: "createdAt" },
          { text: "Options", value: "options" }
        ],
        subdomains: []
      },
      nameservers: [],
      oldNameservers: []
    }
  },
  methods: {
    getEligibleUsers() {
      this.axios
        .get(
          "/api/v1/domains/simple/zone/getEligibleUsers/" +
            this.$route.params.domain
        )
        .then((res) => {
          this.eligibleUsers.users = res.data.rows
          this.eligibleUsers.count = res.data.count
        })
        .catch((e) => {
          AjaxErrorHandler(this.$store)(e)
        })
    },
    removeUserFromDomain(item) {
      this.axios
        .put("/api/v1/domains/simple/zone/addUser", {
          username: item.username,
          domain: this.domain.domain
        })
        .then(() => {
          this.getEligibleUsers()
        })
        .catch((e) => {
          this.getEligibleUsers()
          AjaxErrorHandler(this.$store)(e)
        })
    },
    addUserToDomain() {
      this.axios
        .put("/api/v1/domains/simple/zone/addUser", {
          username: this.user,
          domain: this.domain.domain
        })
        .then(() => {
          this.getEligibleUsers()
          this.user = ""
        })
        .catch((e) => {
          this.getEligibleUsers()
          AjaxErrorHandler(this.$store)(e)
        })
    },
    saveDomainSettings() {
      this.subdomains.loading = true
      this.axios
        .put("/api/v1/domains/simple/zone/changeInternalSettings", {
          domain: this.$route.params.domain,
          subdomains: this.subdomains.enabled,
          subdomainsCreate: this.subdomains.enabledCreate,
          restricted: this.domain.restricted
        })
        .then((res) => {
          this.subdomains.loading = false
          this.subdomains.modal = false
          this.getDNSRecords()
        })
        .catch((e) => {
          this.subdomains.loading = false
          AjaxErrorHandler(this.$store)(e)
        })
    },
    dayjs(params) {
      return dayjs(params)
    },
    addRecord() {
      if (this.subdomains.advanced) {
        this.addRecordAdvanced()
      } else {
        this.addRecordSimple()
      }
    },
    addRecordSimple() {
      this.subdomains.loading = true
      this.axios
        .post("/api/v1/domains/simple/record/create", {
          domain: this.$route.params.domain,
          subdomain: this.subdomains.recordName
        })
        .then((res) => {
          this.subdomains.loading = false
          this.subdomains.modal = false
          this.getDNSRecords()
        })
        .catch((e) => {
          this.simple.loading = false
          AjaxErrorHandler(this.$store)(e)
        })
    },
    addRecordAdvanced() {
      this.axios
        .post("/api/v1/domains/simple/record/create", {
          domain: this.$route.params.domain,
          subdomain: this.subdomains.recordName
        })
        .then((res) => {
          this.subdomains.loading = false
          this.subdomains.modal = false
          this.getDNSRecords()
        })
        .catch((e) => {
          this.simple.loading = false
          AjaxErrorHandler(this.$store)(e)
        })
    },
    getCFNSStatus() {
      this.loading = true
      this.axios
        .get("/api/v1/domains/simple/zone/get/" + this.$route.params.domain)
        .then((res) => {
          this.loading = false
          this.nameservers = res.data.result[0].name_servers
          this.oldNameservers = res.data.result[0].original_name_servers
          this.status = res.data.result[0].status
        })
        .catch((e) => {
          this.loading = false
          AjaxErrorHandler(this.$store)(e)
        })
    },
    getDomain() {
      this.axios
        .get("/api/v1/domains/get/" + this.$route.params.domain)
        .then((res) => {
          this.domain = res.data
          this.subdomains.enabled = res.data.subdomains
          this.subdomains.enabledCreate = res.data.subdomainsCreate
        })
        .catch((e) => {
          AjaxErrorHandler(this.$store)(e)
        })
    },
    getDNSRecords() {
      this.loading = true
      this.axios
        .get("/api/v1/domains/simple/record/get/" + this.$route.params.domain)
        .then((res) => {
          this.loading = false
          this.subdomains.subdomains = res.data
        })
        .catch((e) => {
          this.loading = false
          AjaxErrorHandler(this.$store)(e)
        })
    }
  },
  mounted() {
    this.getDomain()
    this.getCFNSStatus()
    this.getDNSRecords()
    this.getEligibleUsers()
  },
  watch: {
    userSearch() {
      this.axios
        .put("/api/v1/userutils/users/search", {
          query: this.userSearch
        })
        .then((res) => {
          this.userCount = res.data.count
          this.users = res.data.rows
        })
        .catch((e) => {
          AjaxErrorHandler(this.$store)(e)
        })
    }
  }
}
</script>

<style scoped></style>
